import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useLayoutEffect, useRef, useState } from 'react';
import BubbleButton from './BubbleButton';
import './BubbleNestedDropdown.scss';
import { useClickOutside } from './hooks/useClickOutside';
import type { BubbleButtonProps } from './BubbleButton';

export interface NestedDropdownItem<T> {
  key: string;
  element: string;
  submenuItems?: NestedDropdownItem<T>[];
}

interface BubbleNestedDropdownProps<T> extends BubbleButtonProps {
  items: NestedDropdownItem<T>[];
  onItemClick: (action: string, item: any) => void;
}

export default function BubbleNestedDropdown<T>({
  items,
  title,
  onItemClick,
  ...buttonProps
}: BubbleNestedDropdownProps<T>) {
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);
  const [submenuAlignments, setSubmenuAlignments] = useState<Record<string, 'top' | 'bottom'>>({});
  const [mainDropdownCoords, setMainDropdownCoords] = useState<React.CSSProperties>({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });
  const submenuRefs = useRef<Map<string, HTMLUListElement>>(new Map());
  const mainDropdownRef = useRef<HTMLUListElement | null>(null);

  function toggleOpen(key: string, e: React.MouseEvent) {
    e.stopPropagation();
    setOpenKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }

  function resetAllStates() {
    setOpen(false);
    setOpenKeys(new Set());
    setSubmenuAlignments({});
    setMainDropdownCoords({ position: 'fixed', top: -9999, left: -9999 });
  }

  function handleToggleOpen() {
    if (open) {
      resetAllStates();
    } else {
      setOpen(true);
    }
  }

  function handleClick(key: string, item: any, e: React.MouseEvent) {
    e.stopPropagation();
    onItemClick(key, item);
    resetAllStates();
  }

  const clickOusideRef = useClickOutside(() => {
    resetAllStates();
  });

  const buttonContainerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!open || !mainDropdownRef.current || !buttonContainerRef.current) return;

    const dropdownRect = mainDropdownRef.current.getBoundingClientRect();
    const buttonRect = buttonContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const needsToOpenUpward = buttonRect.bottom + dropdownRect.height > viewportHeight;
    const hasSpaceAbove = buttonRect.top - dropdownRect.height >= 0;

    if (needsToOpenUpward && hasSpaceAbove) {
      setMainDropdownCoords({
        position: 'fixed',
        bottom: viewportHeight - buttonRect.top,
        left: buttonRect.left,
      });
    } else {
      setMainDropdownCoords({
        position: 'fixed',
        top: buttonRect.bottom,
        left: buttonRect.left,
      });
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || openKeys.size === 0) return;

    setSubmenuAlignments(prev => {
      const newAlignments = { ...prev };
      let hasChanges = false;

      openKeys.forEach(key => {
        const submenuEl = submenuRefs.current.get(key);
        if (!submenuEl) return;

        const rect = submenuEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const newAlignment = rect.bottom > viewportHeight ? 'bottom' : 'top';

        if (prev[key] !== newAlignment) {
          newAlignments[key] = newAlignment;
          hasChanges = true;
        }
      });

      return hasChanges ? newAlignments : prev;
    });
  }, [openKeys, open]);

  const renderItems = (items: NestedDropdownItem<T>[], depth = 0, parentKey?: string) => {
    const submenuAlignment = parentKey ? submenuAlignments[parentKey] : undefined;

    return (
      <ul
        className="bubble-dropdown-container rounded border border-grey-lightest shadow-lg list-reset bg-white"
        style={
          depth === 0
            ? { ...mainDropdownCoords, minWidth: '150px' }
            : {
                position: 'absolute',
                top: submenuAlignment === 'bottom' ? 'auto' : 0,
                bottom: submenuAlignment === 'bottom' ? 0 : 'auto',
                left: '100%',
                minWidth: '150px',
              }
        }
        ref={el => {
          if (depth === 0 && el) {
            mainDropdownRef.current = el;
          } else if (el && depth > 0 && parentKey) {
            submenuRefs.current.set(parentKey, el);
          }
        }}
      >
        {items.map(item => {
          const hasSubmenu = item.submenuItems && item.submenuItems.length > 0;
          const isOpen = openKeys.has(item.key);

          return (
            <li
              key={item.key}
              className="py-2 px-4 cursor-pointer list-item relative"
              onClick={e => {
                if (hasSubmenu) {
                  toggleOpen(item.key, e);
                } else {
                  handleClick(item.key, item, e);
                }
              }}
            >
              {item.element}
              {hasSubmenu && (
                <FontAwesomeIcon icon="chevron-right" className="ml-2 submenu-icon" fixedWidth />
              )}
              {hasSubmenu && isOpen && (
                <div className="sub-list-item">
                  {renderItems(item.submenuItems!, depth + 1, item.key)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div ref={clickOusideRef} style={{ position: 'relative', display: 'inline-block' }}>
      <div ref={buttonContainerRef}>
        {title && (
          <BubbleButton {...buttonProps} inverse onClick={handleToggleOpen}>
            {title}
          </BubbleButton>
        )}
        {!title && <BubbleButton {...buttonProps} onClick={handleToggleOpen} />}
      </div>
      {open && renderItems(items)}
    </div>
  );
}
